import { useMemo, useCallback } from 'react';
import { useUserContext } from './context';
import { Permission } from '@instamenu/data-access';
import { permissions } from './permissions';

export const usePermissions = () => {
  const { user } = useUserContext();

  const mapPathsToSegments = useMemo(() => {
    return Object.entries(permissions).map(([rulePath, permissions]) => {
      const parts = rulePath === '/' ? [''] : rulePath.split('/');
      return {
        parts: parts.map((part) => (/^\[.*\]$/.test(part) ? '*' : part)),
        permissions: permissions,
      };
    });
  }, []);

  const allUserPerms = useMemo(() => {
    return new Set(Object.values(user?.permissions || {}).flat());
  }, [user?.permissions]);

  const hasSystemPermission = useMemo(() => {
    return allUserPerms.has(Permission.System);
  }, [allUserPerms]);

  const canAccess = useCallback(
    (requiredPermissions: string[] | undefined): boolean => {
      if (hasSystemPermission) return true;
      if (!requiredPermissions || requiredPermissions.length === 0) return false;
      return requiredPermissions.some((reqPerm) => allUserPerms.has(reqPerm));
    },
    [hasSystemPermission, allUserPerms],
  );

  const canAccessPath = (path: string): boolean => {
    const parts = path.split('/');
    let index = 0;
    let matches = mapPathsToSegments;

    if (hasSystemPermission) return true;

    while (index < parts.length) {
      const exactMatches = matches.filter(
        (rule) => rule.parts.length > index && rule.parts[index] === parts[index],
      );

      matches = exactMatches.length
        ? exactMatches
        : matches.filter((rule) => rule.parts.length > index && rule.parts[index] === '*');
      if (matches.length === 0) return false;
      else if (matches.length === 1) return canAccess(matches[0].permissions);
      index++;
    }

    const exactMatch = matches.find((rule) => rule.parts.length === parts.length);
    return canAccess((exactMatch ?? matches[0]).permissions);
  };

  return { canAccessPath };
};
